import { useCreator } from "../creator/CreatorContext";
import { useSession } from "../auth/useSession";
import { Button, Display, Panel, TierBadge } from "../design/primitives";
import css from "./account.module.css";

/**
 * §26. Identity grid, the billing-lives-on-Patreon paragraph, MANAGE ON
 * PATREON, SIGN OUT, and the tier panel with the **authored** blurb (D-9) -
 * not the Patreon description.
 */
export default function Account() {
  const { creator, me } = useCreator();
  const session = useSession();

  const name = session.name ?? me?.name ?? "";
  /*
   * `me` is the authority now. `session.email` is always undefined since task
   * 9 — the session cookie carries a `sub` and nothing else — and the chain is
   * kept only so this line reads the same until task 12 removes the first term.
   */
  const email = session.email ?? me?.email ?? "—";
  const sub = session.sub ?? me?.sub ?? "";
  const tier = me?.tier ?? null;

  return (
    <div className={css.page}>
      <Display size={38} as="h1">
        YOUR ACCOUNT
      </Display>

      <div className={css.identity}>
        <div className={css.cell}>
          <span className={css.label}>NAME</span>
          <span className={css.value}>{name}</span>
        </div>
        <div className={css.cell}>
          <span className={css.label}>EMAIL</span>
          <span className={css.value}>{email}</span>
        </div>
        <div className={css.cell}>
          <span className={css.label}>SIGNED IN VIA</span>
          <span className={css.value}>
            {session.isAuthenticated ? "PATREON" : "NOT SIGNED IN"}
          </span>
        </div>
        <div className={css.cell}>
          <span className={css.label}>ACCOUNT ID</span>
          <span className={css.value}>{sub}</span>
        </div>
      </div>

      {tier && (
        <Panel className={css.tierPanel}>
          <div className={css.tierHead}>
            <TierBadge name={tier.name} />
          </div>
          <p className={css.blurb}>{creator.tierBlurbs[tier.id]}</p>
        </Panel>
      )}

      <p className={css.copy}>
        Billing lives on Patreon. Changing tier, updating a card or cancelling
        all happen there, and this library follows within a few minutes of the
        change landing.
      </p>

      <div className={css.actions}>
        <Button
          variant="gold"
          size="rail"
          onClick={() =>
            window.open(
              `https://www.patreon.com/user/membership`,
              "_blank",
              "noopener"
            )
          }
        >
          MANAGE ON PATREON
        </Button>
        {session.isAuthenticated ? (
          <Button variant="secondary" size="rail" onClick={session.signOut}>
            SIGN OUT
          </Button>
        ) : (
          // Wrapped, not passed by reference: `signIn` now takes a return
          // path, and handing it straight to `onClick` would pass the click
          // event as one. Harmless - `safeReturnTo` refuses a non-string - but
          // it reads as if this screen meant to send a destination, and it
          // does not. Signing in from /account belongs on the default route.
          <Button variant="secondary" size="rail" onClick={() => session.signIn()}>
            SIGN IN WITH PATREON
          </Button>
        )}
      </div>
    </div>
  );
}
